/**
 * AI Provider: choose Claude or GPT, its model and its key, for every bot.
 *
 * The one place the AI is chosen: bots have no model of their own, so what is
 * picked here is what every website answers with. Admin only, like System
 * check. Both vendors' keys can be
 * on file at once, so switching is one click rather than a hunt for a key.
 * Every save is checked against the vendor before anything is stored — a dead
 * key fails here with the vendor's own message instead of in a visitor's chat
 * — and a key never comes back from the server: the field shows a masked hint
 * and stays empty until someone types a new one.
 */

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api/client';
import type {
  AiProviderName,
  AiProviderSettings,
  AiProviderUpdate,
  SettingSource,
} from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Spinner,
} from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { formatDateTime } from '../lib/format';

type Vendor = 'anthropic' | 'openai';

const VENDOR_LABELS: Record<AiProviderName, string> = {
  anthropic: 'Claude API (Anthropic)',
  openai: 'GPT (OpenAI)',
  azure: 'Azure OpenAI',
  echo: 'Echo (test replies)',
};

const VENDOR_NUMBER: Record<Vendor, string> = { anthropic: '1', openai: '2' };

interface ModelChoice {
  id: string;
  label: string;
  description: string;
}

/**
 * The models offered per vendor; "Other" takes any id the vendor serves, and
 * saving checks it. The Claude list is the one each bot used to pick from,
 * tested against a real EpoxyTops question on 2026-10-12.
 */
const MODEL_CHOICES: Record<Vendor, ModelChoice[]> = {
  anthropic: [
    {
      id: 'claude-sonnet-5',
      label: 'Claude Sonnet 5',
      description: 'What every bot has used so far. Good answers at a moderate cost.',
    },
    {
      id: 'claude-sonnet-5-5',
      label: 'Claude Sonnet 5.5',
      description: 'Newer Sonnet: the same price as Sonnet 5, often better answers.',
    },
    {
      id: 'claude-haiku-5-5',
      label: 'Claude Haiku 5.5',
      description:
        'Fastest and about twenty times cheaper. Fine for simple questions; less reliable on detailed comparisons.',
    },
    {
      id: 'claude-opus-5-5',
      label: 'Claude Opus 5.5',
      description:
        'Most capable and about twice the cost of Sonnet, and slower. Only worth it if answers need to be more thorough.',
    },
  ],
  openai: [
    {
      id: 'gpt-5',
      label: 'GPT-5',
      description: "OpenAI's main model. The default for GPT.",
    },
    {
      id: 'gpt-5-mini',
      label: 'GPT-5 mini',
      description: 'Faster and cheaper. Fine for simple questions.',
    },
  ],
};

interface Draft {
  provider: AiProviderName;
  anthropicKey: string;
  anthropicModel: string;
  maxTokens: string;
  openaiKey: string;
  openaiModel: string;
}

function draftFrom(settings: AiProviderSettings): Draft {
  return {
    provider: settings.provider,
    anthropicKey: '',
    anthropicModel: settings.model,
    maxTokens: String(settings.max_tokens),
    openaiKey: '',
    openaiModel: settings.openai_model,
  };
}

/** Only what changed, so an untouched key is never re-sent or cleared. */
function changes(draft: Draft, settings: AiProviderSettings): AiProviderUpdate {
  const update: AiProviderUpdate = {};
  if (draft.provider !== settings.provider) update.provider = draft.provider;
  if (draft.anthropicKey.trim()) update.api_key = draft.anthropicKey.trim();
  if (draft.anthropicModel.trim() !== settings.model)
    update.model = draft.anthropicModel.trim();
  if (Number(draft.maxTokens) !== settings.max_tokens)
    update.max_tokens = Number(draft.maxTokens);
  if (draft.openaiKey.trim()) update.openai_api_key = draft.openaiKey.trim();
  if (draft.openaiModel.trim() !== settings.openai_model)
    update.openai_model = draft.openaiModel.trim();
  return update;
}

/** A client-side check for what the server would reject as malformed. */
function problem(draft: Draft): string | null {
  if (!draft.anthropicModel.trim() || !draft.openaiModel.trim())
    return 'A model name cannot be empty.';
  const tokens = Number(draft.maxTokens);
  if (!Number.isInteger(tokens) || tokens < 64 || tokens > 128000)
    return 'Claude reply limit must be a whole number from 64 to 128000.';
  return null;
}

function errorText(caught: unknown, fallback: string): string {
  return caught instanceof Error && typeof caught.message === 'string'
    ? caught.message
    : fallback;
}

export function AiProvider(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [settings, setSettings] = useState<AiProviderSettings | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'save' | 'test' | 'reset' | 'clear' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const apply = useCallback((next: AiProviderSettings) => {
    setSettings(next);
    setDraft(draftFrom(next));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      apply(await api.aiProvider());
      setError(null);
    } catch (caught) {
      setError(errorText(caught, 'Could not load the AI provider settings'));
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  async function run(
    kind: NonNullable<typeof busy>,
    action: () => Promise<AiProviderSettings | string>,
  ): Promise<void> {
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      const result = await action();
      if (typeof result === 'string') setNotice(result);
      else apply(result);
    } catch (caught) {
      setError(errorText(caught, 'The request failed'));
    } finally {
      setBusy(null);
    }
  }

  if (!isAdmin) {
    return (
      <Card>
        <EmptyState
          title="AI Provider is for administrators."
          hint="It manages the API keys every bot uses."
        />
      </Card>
    );
  }
  if (loading) return <Spinner label="Loading AI provider…" />;
  if (!settings || !draft) return <ErrorNotice message={error ?? 'Not available'} />;

  const pending = changes(draft, settings);
  const dirty = Object.keys(pending).length > 0;
  const invalid = problem(draft);
  const set = (patch: Partial<Draft>): void => setDraft({ ...draft, ...patch });

  function handleSave(): void {
    if (invalid) {
      setError(invalid);
      return;
    }
    const switching = pending.provider
      ? ` Every bot now answers with ${VENDOR_LABELS[pending.provider]}.`
      : '';
    void run('save', async () => {
      const next = await api.updateAiProvider(pending);
      setNotice(`Saved and verified with the vendor.${switching}`);
      return next;
    });
  }

  function handleClear(vendor: Vendor): void {
    const label = VENDOR_LABELS[vendor];
    if (!window.confirm(`Remove the ${label} key saved here and use the one in .env?`))
      return;
    void run('clear', async () => {
      const next = await api.updateAiProvider(
        vendor === 'anthropic' ? { api_key: '' } : { openai_api_key: '' },
      );
      setNotice(`${label} key removed; the .env value is in force.`);
      return next;
    });
  }

  function handleReset(): void {
    if (
      !window.confirm(
        'Remove every setting saved on this page, including both keys, and go back to the .env configuration?',
      )
    )
      return;
    void run('reset', async () => {
      const next = await api.resetAiProvider();
      setNotice('Back on the .env configuration.');
      return next;
    });
  }

  function handleTest(): void {
    void run('test', async () => {
      const result = await api.testAiProvider();
      if (!result.ok) throw new Error(result.detail);
      return `${VENDOR_LABELS[result.provider]}: ${result.detail}`;
    });
  }

  const envOnly = settings.provider === 'azure' || settings.provider === 'echo';

  return (
    <div>
      <PageHeader
        title="AI Provider"
        description="Which AI answers visitors on every website, and the API keys it uses. Saving checks the key with the vendor first."
        actions={
          <>
            <Button variant="secondary" onClick={handleTest} disabled={busy !== null}>
              {busy === 'test' ? 'Testing…' : 'Test current'}
            </Button>
            <Button onClick={handleSave} disabled={!dirty || busy !== null}>
              {busy === 'save' ? 'Verifying…' : 'Save'}
            </Button>
          </>
        }
      />

      {error && (
        <div className="mb-4">
          <ErrorNotice message={error} />
        </div>
      )}
      {notice && (
        <div
          role="status"
          className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
        >
          {notice}
        </div>
      )}
      {settings.unreadable_overrides.length > 0 && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          A saved key can no longer be decrypted (the server&apos;s JWT_SECRET changed),
          so the .env value is in use. Paste the key again to fix it.
        </div>
      )}
      {envOnly && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {VENDOR_LABELS[settings.provider]} is selected in .env. Choose Claude or GPT
          below to switch.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <VendorCard
          vendor="anthropic"
          selected={draft.provider === 'anthropic'}
          active={settings.provider === 'anthropic'}
          onSelect={() => set({ provider: 'anthropic' })}
          keySet={settings.anthropic_api_key_set}
          keyHint={settings.anthropic_api_key_hint}
          keySource={settings.api_key_source}
          keyValue={draft.anthropicKey}
          onKey={(value) => set({ anthropicKey: value })}
          keyPlaceholder="sk-ant-…"
          keyHelp="From console.anthropic.com → API keys."
          model={draft.anthropicModel}
          modelSource={settings.model_source}
          onModel={(value) => set({ anthropicModel: value })}
          onClear={() => handleClear('anthropic')}
          busy={busy !== null}
        >
          <label className="mt-3 block text-sm">
            <span className="font-medium text-slate-700">Reply limit (tokens)</span>
            <SourceTag source={settings.max_tokens_source} />
            <input
              type="number"
              min={64}
              max={128000}
              step={1}
              value={draft.maxTokens}
              onChange={(event) => set({ maxTokens: event.target.value })}
              className="mt-1 block w-40 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            />
            <span className="mt-1 block text-xs text-slate-500">
              Thinking counts against it; below about 4096 answers get cut off.
            </span>
          </label>
        </VendorCard>

        <VendorCard
          vendor="openai"
          selected={draft.provider === 'openai'}
          active={settings.provider === 'openai'}
          onSelect={() => set({ provider: 'openai' })}
          keySet={settings.openai_api_key_set}
          keyHint={settings.openai_api_key_hint}
          keySource={settings.openai_api_key_source}
          keyValue={draft.openaiKey}
          onKey={(value) => set({ openaiKey: value })}
          keyPlaceholder="sk-…"
          keyHelp="From platform.openai.com → API keys. A ChatGPT subscription is not an API key."
          model={draft.openaiModel}
          modelSource={settings.openai_model_source}
          onModel={(value) => set({ openaiModel: value })}
          onClear={() => handleClear('openai')}
          busy={busy !== null}
        />
      </div>

      <p className="mt-4 text-xs text-slate-500">
        Every website answers with the AI and model chosen here. To make replies shorter
        or longer, say so in a bot&apos;s Settings → Behaviour &amp; Instructions.
      </p>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 text-xs text-slate-500">
        <span>
          {settings.updated_at
            ? `Last changed ${formatDateTime(settings.updated_at)} by ${settings.updated_by ?? 'unknown'}.`
            : 'Nothing saved here yet: everything comes from .env.'}
        </span>
        <Button
          variant="danger"
          onClick={handleReset}
          disabled={busy !== null || !settings.updated_at}
        >
          {busy === 'reset' ? 'Resetting…' : 'Reset to .env'}
        </Button>
      </div>
    </div>
  );
}

function SourceTag({ source }: { source: SettingSource }): JSX.Element {
  return (
    <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
      {source === 'database' ? 'saved here' : 'from .env'}
    </span>
  );
}

interface VendorCardProps {
  vendor: Vendor;
  selected: boolean;
  active: boolean;
  onSelect: () => void;
  keySet: boolean;
  keyHint: string;
  keySource: SettingSource;
  keyValue: string;
  onKey: (value: string) => void;
  keyPlaceholder: string;
  keyHelp: string;
  model: string;
  modelSource: SettingSource;
  onModel: (value: string) => void;
  onClear: () => void;
  busy: boolean;
  children?: JSX.Element;
}

function VendorCard(props: VendorCardProps): JSX.Element {
  const { vendor, selected, active, keySet, keyHint } = props;

  return (
    <Card className={`p-5 ${selected ? 'ring-2 ring-brand-blue' : ''}`}>
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="radio"
          name="provider"
          className="mt-1"
          checked={selected}
          onChange={props.onSelect}
        />
        <span>
          <span className="block text-base font-semibold text-slate-900">
            {VENDOR_NUMBER[vendor]}. {VENDOR_LABELS[vendor]}
            {active && (
              <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                In use
              </span>
            )}
          </span>
          <span className="block text-xs text-slate-500">
            {keySet ? `Key on file: ${keyHint}` : 'No key on file yet.'}
          </span>
        </span>
      </label>

      <label className="mt-4 block text-sm">
        <span className="font-medium text-slate-700">API key</span>
        {keySet && <SourceTag source={props.keySource} />}
        <input
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={props.keyValue}
          onChange={(event) => props.onKey(event.target.value)}
          placeholder={
            keySet ? 'Leave blank to keep the current key' : props.keyPlaceholder
          }
          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-1.5 font-mono text-sm"
        />
        <span className="mt-1 block text-xs text-slate-500">{props.keyHelp}</span>
      </label>
      {props.keySource === 'database' && keySet && (
        <button
          type="button"
          onClick={props.onClear}
          disabled={props.busy}
          className="mt-1 text-xs text-slate-500 underline hover:text-slate-700 disabled:no-underline"
        >
          Remove the key saved here
        </button>
      )}

      <ModelPicker
        vendor={vendor}
        model={props.model}
        source={props.modelSource}
        onModel={props.onModel}
      />

      {props.children}
    </Card>
  );
}

interface ModelPickerProps {
  vendor: Vendor;
  model: string;
  source: SettingSource;
  onModel: (value: string) => void;
}

function ModelPicker({
  vendor,
  model,
  source,
  onModel,
}: ModelPickerProps): JSX.Element {
  const choices = MODEL_CHOICES[vendor];
  const listed = choices.some((choice) => choice.id === model);
  // "Other" stays open while its text is being edited, even when empty or
  // briefly matching a listed id; an unlisted model (from .env) always opens it.
  const [otherPicked, setOther] = useState(false);
  const other = otherPicked || !listed;
  const name = `model-${vendor}`;
  const row = (on: boolean): string =>
    `flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 ${
      on ? 'border-brand-blue bg-brand-light' : 'border-slate-200 hover:bg-slate-50'
    }`;

  return (
    <fieldset className="mt-3">
      <legend className="text-sm font-medium text-slate-700">
        Model
        <SourceTag source={source} />
      </legend>
      <div className="mt-2 space-y-2">
        {choices.map((choice) => (
          <label key={choice.id} className={row(!other && model === choice.id)}>
            <input
              type="radio"
              name={name}
              className="mt-1"
              checked={!other && model === choice.id}
              onChange={() => {
                setOther(false);
                onModel(choice.id);
              }}
            />
            <span>
              <span className="block text-sm font-medium text-slate-800">
                {choice.label}
              </span>
              <span className="block text-xs text-slate-500">{choice.description}</span>
            </span>
          </label>
        ))}
        <label className={row(other)}>
          <input
            type="radio"
            name={name}
            className="mt-1"
            checked={other}
            onChange={() => setOther(true)}
          />
          <span className="flex-1">
            <span className="block text-sm font-medium text-slate-800">
              Other model
            </span>
            {other && (
              <input
                spellCheck={false}
                value={model}
                onChange={(event) => onModel(event.target.value)}
                placeholder={vendor === 'anthropic' ? 'claude-…' : 'gpt-…'}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-1.5 font-mono text-sm"
              />
            )}
          </span>
        </label>
      </div>
    </fieldset>
  );
}
